/**
 * vpv-simple.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Scoped product gallery + form using native Custom Elements.
 * Zero globals exposed. No jQuery. No conflicts with legacy theme JS.
 *
 * Custom elements defined:
 *   <vpv-simple-gallery>  — gallery carousel w/ image + video support
 *   <vpv-simple-form>     — variant picker + ATC + cart drawer/count update
 *
 * Inter-element communication:
 *   vpv-simple-form  → dispatches  vpv:variant-change:{sectionId}
 *   vpv-simple-gallery → listens   vpv:variant-change:{sectionId}
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Shared utilities ────────────────────────────────────────────────────────

  /** Parse the embedded JSON blob for this section. */
  function getProductData(sectionId) {
    const el = document.getElementById(`vpv-simple-data-${sectionId}`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[vpv-simple] JSON parse error', e);
      return null;
    }
  }

  /**
   * Format cents as a currency string.
   * Uses Intl.NumberFormat so it's locale- and currency-aware without
   * relying on theme globals.
   */
  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      ? window.Shopify.currency.active
      : 'USD';
    const isWhole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(navigator.language || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
      }).format(cents / 100);
    } catch (_) {
      return '$' + (isWhole ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2));
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  //  <vpv-simple-gallery>
  // ═══════════════════════════════════════════════════════════════════════════

  class VpvSimpleGallery extends HTMLElement {
    constructor() {
      super();
      this._index   = 0;
      this._media   = [];
      this._data    = null;
      this._cleanup = [];
    }

    connectedCallback() {
      const sectionId = this.dataset.section;
      this._data = getProductData(sectionId);
      if (!this._data) return;

      this._slidesEl  = this.querySelector('.vpv-simple__slides');
      this._dotsEl    = this.querySelector('.vpv-simple__dots');
      this._infoGridEl = this.querySelector('[data-vpv-info-grid]');
      this._prevBtn   = this.querySelector('.vpv-simple__arrow--prev');
      this._nextBtn   = this.querySelector('.vpv-simple__arrow--next');

      // Arrow clicks
      const onPrev = () => this._goTo(this._index - 1);
      const onNext = () => this._goTo(this._index + 1);
      this._prevBtn?.addEventListener('click', onPrev);
      this._nextBtn?.addEventListener('click', onNext);

      // Touch swipe
      this._initSwipe();

      // Lightbox on image click/tap/pinch
      this._initLightbox();

      // Keyboard navigation when gallery is focused
      const onKey = (e) => {
        if (e.key === 'ArrowLeft')  this._goTo(this._index - 1);
        if (e.key === 'ArrowRight') this._goTo(this._index + 1);
      };
      this.addEventListener('keydown', onKey);

      // Variant change from form element
      const onVariantChange = (e) => this._onVariantChange(e.detail.variant);
      document.addEventListener(`vpv:variant-change:${sectionId}`, onVariantChange);
      this._cleanup.push(() => document.removeEventListener(`vpv:variant-change:${sectionId}`, onVariantChange));

      // Load initial media
      this._loadInitialMedia();
    }

    disconnectedCallback() {
      this._cleanup.forEach(fn => fn());
    }

    // ── Media loading ─────────────────────────────────────────────────────────

    _loadInitialMedia() {
      const d = this._data;

      // Case 1: single variant → native product.media + product-level info grid
      if (d.has_only_default_variant) {
        this._setMedia(d.native_media);
        this._renderInfoGrid(d.product_info_grid || []);
        return;
      }

      const currentId = parseInt(this.dataset.currentVariant, 10);
      const variant   = d.variants.find(v => v.id === currentId) || d.variants[0];
      this._setMedia(this._resolveMedia(variant));
      this._renderInfoGrid(this._resolveInfoGrid(variant));
    }

    _setMedia(mediaArr) {
      this._media = Array.isArray(mediaArr) ? mediaArr : [];
      this._index = 0;
      this._renderSlides();
      this._renderDots();
      this._updateArrows();
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

_renderSlides() {
  if (!this._slidesEl) return;
  this._slidesEl.innerHTML = this._media
    .map((m, i) => this._buildSlide(m, i))
    .join('');

  // Fix: muted attribute doesn't set the .muted property via innerHTML
  this._slidesEl.querySelectorAll('video').forEach(v => { v.muted = true; });

  this._activateSlide(0, false);
}
    _buildSlide(m, i) {
      const activeClass = i === 0 ? ' is-active' : '';

      if (m.type === 'video') {
        // ── Native HTML5 video ──────────────────────────────────────────────
        return `<div class="vpv-simple__slide${activeClass}" data-index="${i}">
          <video
            class="vpv-simple__media vpv-simple__media--video"
            src="${this._esc(m.url)}"
            poster="${this._esc(m.preview || '')}"
            playsinline
            autoplay
            loop
            muted
            preload="auto"
            aria-label="${this._esc(m.alt || 'Product video')}"
          ></video>
        </div>`;
      }

      if (m.type === 'external_video') {
        // ── YouTube / Vimeo embed ────────────────────────────────────────────
        const host = m.host || 'youtube';
        const src  = host === 'vimeo'
          ? `https://player.vimeo.com/video/${m.url}?autoplay=0&title=0&byline=0`
          : `https://www.youtube.com/embed/${m.url}?autoplay=0&rel=0`;
        return `<div class="vpv-simple__slide vpv-simple__slide--video${activeClass}" data-index="${i}">
          <div class="vpv-simple__media vpv-simple__media--iframe">
            <iframe
              src="${src}"
              frameborder="0"
              allow="autoplay; encrypted-media"
              allowfullscreen
              loading="lazy"
              title="${this._esc(m.alt || 'Product video')}"
            ></iframe>
          </div>
        </div>`;
      }

      // ── Image ──────────────────────────────────────────────────────────────
      return `<div class="vpv-simple__slide${activeClass}" data-index="${i}">
        <button
          class="vpv-simple__lightbox-trigger"
          type="button"
          data-lightbox-index="${i}"
          aria-label="View image ${i + 1} fullscreen"
        ><img
            class="vpv-simple__media vpv-simple__media--image"
            src="${this._esc(m.url)}"
            alt="${this._esc(m.alt || '')}"
            loading="${i === 0 ? 'eager' : 'lazy'}"
            decoding="async"
            draggable="false"
          ></button>
      </div>`;
    }

    _renderDots() {
      if (!this._dotsEl) return;
      if (this._media.length <= 1) {
        this._dotsEl.innerHTML = '';
        return;
      }
      this._dotsEl.innerHTML = this._media
        .map((_, i) => `<button
            class="vpv-simple__dot${i === 0 ? ' is-active' : ''}"
            data-index="${i}"
            aria-label="Go to image ${i + 1}"
            aria-pressed="${i === 0}"
          ></button>`)
        .join('');

      this._dotsEl.querySelectorAll('.vpv-simple__dot').forEach(btn => {
        btn.addEventListener('click', () => this._goTo(parseInt(btn.dataset.index, 10)));
      });
    }

    _renderThumbs() {} // no-op — replaced by _renderDots

    // ── Navigation ────────────────────────────────────────────────────────────

    _goTo(index) {
      const count = this._media.length;
      if (count === 0) return;
      this._index = ((index % count) + count) % count;
      this._activateSlide(this._index, true);
      this._updateArrows();
    }

    _activateSlide(index, animate) {
  if (!animate && this._slidesEl) {
    this._slidesEl.querySelectorAll('.vpv-simple__slide').forEach(s => {
      s.style.transition = 'none';
    });
    void this._slidesEl.offsetWidth;
  }

  this._slidesEl?.querySelectorAll('.vpv-simple__slide').forEach((s, i) => {
    const isActive = i === index;
    s.classList.toggle('is-active', isActive);

    const vid = s.querySelector('video');
    if (vid) {
      if (isActive) {
        vid.muted = true;
        // rAF ensures the element is painted/visible before play() fires
        requestAnimationFrame(() => vid.play().catch(() => {}));
      } else {
        vid.pause();
      }
    }

    if (!animate) s.style.transition = '';
  });

  this._dotsEl?.querySelectorAll('.vpv-simple__dot').forEach((t, i) => {
    const active = i === index;
    t.classList.toggle('is-active', active);
    t.setAttribute('aria-pressed', String(active));
  });
}

    _updateArrows() {
      const single = this._media.length <= 1;
      if (this._prevBtn) this._prevBtn.disabled = single;
      if (this._nextBtn) this._nextBtn.disabled = single;
      this.querySelector('.vpv-simple__gallery-main')
        ?.classList.toggle('is-single-slide', single);
    }

    // ── Lightbox (PhotoSwipe v5) ───────────────────────────────────────────────

    /**
     * Attach a single delegated click listener on the slides container.
     * Works across variant-driven re-renders because we never re-bind —
     * the listener stays on _slidesEl permanently.
     */
    _initLightbox() {
      const open = (e) => {
        const trigger = e.target.closest('.vpv-simple__lightbox-trigger');
        if (!trigger) return;
        this._triggerLightbox(parseInt(trigger.dataset.lightboxIndex, 10));
      };

      this._slidesEl?.addEventListener('click', open);

      // Keyboard: Enter or Space on focused trigger
      this._slidesEl?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const trigger = e.target.closest('.vpv-simple__lightbox-trigger');
        if (!trigger) return;
        e.preventDefault();
        this._triggerLightbox(parseInt(trigger.dataset.lightboxIndex, 10));
      });
    }

    /**
     * Build an images-only items array for PhotoSwipe and open at the correct
     * position.  Video and external_video slides are silently skipped —
     * clicking them does nothing (the <video> handles its own interaction).
     */
    async _triggerLightbox(mediaIndex) {
  const items = [];
  let lightboxIndex = 0;

  this._media.forEach((m, i) => {
    if (i === mediaIndex) lightboxIndex = items.length;

    if (m.type === 'image') {
      items.push({
        src:   m.url,
        msrc:  m.preview || m.url,
        alt:   m.alt || '',
        width: 1200,
        height: 1200,
      });

    } else if (m.type === 'video') {
      items.push({
        html: `<div class="vpv-pswp-video-wrap" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">
          <video
            src="${this._esc(m.url)}"
            poster="${this._esc(m.preview || '')}"
            controls
            playsinline
            loop
            preload="auto"
            style="max-width:100%;max-height:80vh;display:block;outline:none;"
          ></video>
        </div>`,
      });

    } else if (m.type === 'external_video') {
      const host = m.host || 'youtube';
      const src  = host === 'vimeo'
        ? `https://player.vimeo.com/video/${m.url}?autoplay=1&title=0&byline=0`
        : `https://www.youtube.com/embed/${m.url}?autoplay=1&rel=0`;
      items.push({
        html: `<div class="vpv-pswp-video-wrap" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">
          <iframe
            src="${src}"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            style="width:min(100%,900px);aspect-ratio:16/9;display:block;"
          ></iframe>
        </div>`,
      });
    }
  });

  if (!items.length) return;

  const PhotoSwipe = await this._loadPswp();
  if (!PhotoSwipe) return;

  // Pause the gallery's active video while lightbox is open
  const activeGalleryVid = this._slidesEl
    ?.querySelector(`.vpv-simple__slide.is-active video`);
  if (activeGalleryVid) activeGalleryVid.pause();

  const pswp = new PhotoSwipe({
    dataSource:          items,
    index:               lightboxIndex,
    bgOpacity:           0.95,
    zoom:                true,
    maxZoomLevel:        4,
    initialZoomLevel:    'fit',
    secondaryZoomLevel:  2,
    wheelToZoom:         true,
    pinchToClose:        true,
    closeOnVerticalDrag: true,
    padding:             { top: 20, bottom: 20, left: 20, right: 20 },
  });

  // Resume gallery video when lightbox closes
  pswp.on('destroy', () => {
    if (activeGalleryVid) {
      activeGalleryVid.muted = true;
      activeGalleryVid.play().catch(() => {});
    }
  });

  pswp.init();
}

    /**
     * Lazy-load PhotoSwipe v5 as an ES module — no global pollution,
     * no conflict with the v4 instance used by the legacy gallery.
     * The promise is cached on window so multiple gallery instances share it.
     */
    async _loadPswp() {
      // Inject CSS once per page
      if (!document.getElementById('vpv-pswp-css')) {
        const link = Object.assign(document.createElement('link'), {
          id:   'vpv-pswp-css',
          rel:  'stylesheet',
          href: 'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.css',
        });
        document.head.appendChild(link);
      }

      // Cache the module promise globally
      if (!window.__vpvPswpPromise) {
        window.__vpvPswpPromise = import(
          'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.esm.min.js'
        )
          .then(m => m.default)
          .catch(err => {
            console.error('[vpv-simple] PhotoSwipe load failed:', err);
            window.__vpvPswpPromise = null;
            return null;
          });
      }

      return window.__vpvPswpPromise;
    }

    // ── Touch swipe ───────────────────────────────────────────────────────────

    _initSwipe() {
      let startX = 0;
      let startY = 0;
      this._slidesEl?.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this._slidesEl?.addEventListener('touchend', (e) => {
        const dx = startX - e.changedTouches[0].clientX;
        const dy = startY - e.changedTouches[0].clientY;
        // Only swipe if mostly horizontal
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          dx > 0 ? this._goTo(this._index + 1) : this._goTo(this._index - 1);
        }
      }, { passive: true });
    }

    // ── Variant change handler ────────────────────────────────────────────────

    _onVariantChange(variant) {
      if (this._data.has_only_default_variant) return;
      this._setMedia(this._resolveMedia(variant));
      this._renderInfoGrid(this._resolveInfoGrid(variant));
    }

    /**
     * Info grid resolution:
     *  1. variant.info_grid has items → use them
     *  2. otherwise → fall back to product_info_grid
     */
    _resolveInfoGrid(variant) {
      if (variant && variant.info_grid && variant.info_grid.length) {
        return variant.info_grid;
      }
      return this._data.product_info_grid || [];
    }

    /**
     * Render up to 2 files into the info grid container.
     * Supports image and video file types.
     */
   _renderInfoGrid(items) {
  if (!this._infoGridEl) return;
  if (!items || !items.length) {
    this._infoGridEl.innerHTML = '';
    return;
  }
  this._infoGridEl.innerHTML = items.slice(0, 2).map(item => {
    if (item.type === 'video') {
      return `<div class="meta_img meta_video">
        <video
          src="${this._esc(item.url)}"
          autoplay
          muted
          loop
          playsinline
          style="display:block;object-fit:cover;width:100%;height:100%;"
        ></video>
      </div>`;
    }
    return `<div class="meta_img">
      <img
        src="${this._esc(item.url)}"
        alt="${this._esc(item.alt || '')}"
        width="100%"
        height="auto"
        loading="lazy"
        decoding="async"
      >
    </div>`;
  }).join('');

  // Fix: muted property must be set via JS, not just the attribute
  this._infoGridEl.querySelectorAll('video').forEach(v => { v.muted = true; });
}

    /**
     * Three-way gallery resolution for multi-variant products:
     *  1. custom.variant_gallery metafield has files → use them
     *  2. metafield empty, variant has its own image  → wrap in single-item array
     *  3. neither                                     → fall back to native_media
     */
    _resolveMedia(variant) {
      if (variant && variant.gallery && variant.gallery.length) {
        return variant.gallery;
      }
      if (variant && variant.variant_image && variant.variant_image.url) {
        return [variant.variant_image];
      }
      return this._data.native_media;
    }

    // ── Utility ───────────────────────────────────────────────────────────────

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  //  <vpv-simple-form>
  // ═══════════════════════════════════════════════════════════════════════════

  class VpvSimpleForm extends HTMLElement {
    constructor() {
      super();
      this._selectedOptions = [];
      this._currentVariant  = null;
      this._data            = null;
      this._busy            = false;
    }

    connectedCallback() {
      this._sectionId = this.dataset.section;
      this._data      = getProductData(this._sectionId);
      if (!this._data) return;

      // DOM refs
      this._form        = this.querySelector('[data-vpv-form]');
      this._variantInput = this.querySelector('[data-vpv-variant-id]');
      this._atcBtn      = this.querySelector('[data-vpv-atc]');
      this._atcText     = this.querySelector('[data-vpv-atc-text]');
      this._priceEls   = this.querySelectorAll('[data-vpv-price]');
      this._compareEls = this.querySelectorAll('[data-vpv-compare-price]');
      this._saleBadges = this.querySelectorAll('[data-vpv-sale-badge]');
      this._qtyInput    = this.querySelector('[data-vpv-qty]');

      // Resolve initial variant
      const initId = parseInt(this.dataset.currentVariant, 10);
      this._currentVariant = this._data.variants.find(v => v.id === initId) || this._data.variants[0];
      if (this._currentVariant) {
        this._selectedOptions = [
          this._currentVariant.option1,
          this._currentVariant.option2,
          this._currentVariant.option3,
        ].filter(v => v != null);
      }

      // Bind option buttons
      this.querySelectorAll('[data-option-index]').forEach(btn => {
        btn.addEventListener('click', () => {
          this._onOptionClick(btn);
        });
      });

      // Quantity controls
      this.querySelector('[data-vpv-qty-dec]')?.addEventListener('click', () => {
        if (this._qtyInput) {
          this._qtyInput.value = Math.max(1, parseInt(this._qtyInput.value || 1) - 1);
        }
      });
      this.querySelector('[data-vpv-qty-inc]')?.addEventListener('click', () => {
        if (this._qtyInput) {
          this._qtyInput.value = parseInt(this._qtyInput.value || 1) + 1;
        }
      });

      // ATC form submit — only proceed if the submitter is the ATC button itself,
      // not any other button (e.g. accordions) that may live inside the form.
      this._form?.addEventListener('submit', (e) => {
        e.preventDefault();
        if (e.submitter !== this._atcBtn) return;
        if (!this._busy) this._addToCart();
      });
    }

    // ── Option selection ──────────────────────────────────────────────────────

    _onOptionClick(clickedBtn) {
      const optionIndex = parseInt(clickedBtn.dataset.optionIndex, 10);
      const value       = clickedBtn.dataset.value;

      this._selectedOptions[optionIndex] = value;

      // Update button states within this option group
      const group = clickedBtn.closest('[data-vpv-group]');
      group?.querySelectorAll('[data-option-index]').forEach(btn => {
        const sel = btn.dataset.value === value;
        btn.classList.toggle('is-selected', sel);
        btn.setAttribute('aria-pressed', String(sel));
      });

      // Update selected label (colour option)
      const labelEl = this.querySelector(`[data-vpv-selected-label="${optionIndex}"]`);
      if (labelEl) labelEl.textContent = value;

      const variant = this._findVariant();
      if (variant) {
        this._currentVariant = variant;
        this._updateVariantUI(variant);
        this._dispatchVariantChange(variant);
      } else {
        this._setButtonState({ available: false, text: 'Unavailable' });
      }
    }

    _findVariant() {
      return this._data.variants.find(v => {
        if (v.enabled === false) return false;
        const opts = [v.option1, v.option2, v.option3].filter(o => o != null);
        return opts.every((o, i) => o === this._selectedOptions[i]);
      }) || null;
    }

    // ── Variant UI update ─────────────────────────────────────────────────────

    _updateVariantUI(variant) {
      // Hidden input
      if (this._variantInput) {
        this._variantInput.value = variant.id;

      }

      // Fire subscription signals only if a widget is present on this page.
      // The subscription app's global JS crashes when its widget element is
      // absent, and synchronous event listener exceptions abort our update chain.
      const hasSubscriptionWidget = !!(
        this.querySelector('[data-rc-widget]') ||
        this.querySelector('[data-skio-widget]') ||
        this.querySelector('.seal-subscriptions') ||
        this.querySelector('[data-bold-subscriptions]') ||
        this.querySelector('subscribe-button')
      );

      if (hasSubscriptionWidget) {
        try {
          this._variantInput?.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          console.warn('[vpv-simple] subscription input change threw:', e);
        }
        try {
          this._form?.dispatchEvent(new CustomEvent('variant:changed', {
            bubbles: true,
            detail: { variant },
          }));
        } catch (e) {
          console.warn('[vpv-simple] variant:changed threw:', e);
        }
      }

      // URL (no reload)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}

      // Price
      this._priceEls.forEach(el => { el.textContent = formatMoney(variant.price); });

      // Compare-at price + sale badge
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this._compareEls.forEach(el => {
        el.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        el.toggleAttribute('hidden', !onSale);
      });
      this._saleBadges.forEach(el => el.toggleAttribute('hidden', !onSale));

      // Preorder shipping message — update the single JS-controlled container
      const preorderEl     = this.querySelector('[data-vpv-preorder-message]');
      const preorderTextEl = this.querySelector('[data-vpv-preorder-text]');
      if (preorderEl && preorderTextEl) {
        const msg = variant.preorder_message || '';
        preorderTextEl.textContent = msg;
        preorderEl.classList.toggle('preorder-hide', !msg);
      }

      // Button state
      this._setButtonState({
        available: variant.available,
        text: variant.available
          ? (this.dataset.atcText || 'Add to Cart')
          : (this.dataset.soldoutText || 'Sold Out'),
      });
    }

    _setButtonState({ available, text }) {
      if (!this._atcBtn) return;
      this._atcBtn.disabled = !available;
      this._atcBtn.classList.toggle('is-sold-out', !available);
      if (this._atcText && text) this._atcText.textContent = text;
    }

    // ── Add to cart ───────────────────────────────────────────────────────────

    async _addToCart() {
      if (!this._currentVariant || !this._currentVariant.available) return;

      const variantId = this._currentVariant.id;
      const qty       = this._qtyInput ? Math.max(1, parseInt(this._qtyInput.value) || 1) : 1;

      // Loading state
      this._busy = true;
      this._atcBtn?.classList.add('is-loading');
      if (this._atcBtn) this._atcBtn.disabled = true;

      try {
        const res = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept':        'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || `HTTP ${res.status}`);
        }

        // Success — update cart count, open drawer
        await this._refreshCartCount();
        this._openCartDrawer();

        // Restore button
        this._atcBtn?.classList.remove('is-loading');
        this._setButtonState({
          available: true,
          text: this.dataset.atcText || 'Add to Cart',
        });

      } catch (err) {
        console.error('[vpv-simple] ATC failed:', err);
        this._atcBtn?.classList.remove('is-loading');
        this._setButtonState({ available: true, text: 'Error — Try Again' });
        setTimeout(() => {
          if (this._currentVariant) this._updateVariantUI(this._currentVariant);
        }, 2500);
      } finally {
        this._busy = false;
      }
    }

    // ── Cart count ────────────────────────────────────────────────────────────

    async _refreshCartCount() {
      try {
        const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        const count = cart.item_count;

        // Update any element matching common Shopify cart-count selectors
        const selectors = [
          '.cart-count',
          '.cart-count-bubble',
          '.header__cart-count',
          '[data-cart-count]',
          '[data-cart-item-count]',
          '#cart-count',
          '.js-cart-count',
          '.cart__count',
        ];
        document.querySelectorAll(selectors.join(', ')).forEach(el => {
          el.textContent = count;
          el.setAttribute('data-count', count);
          if (count > 0) el.removeAttribute('hidden');
        });

        // Standard Shopify + theme events
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart },
        }));

      } catch (e) {
        console.warn('[vpv-simple] Cart count refresh failed', e);
      }
    }

    // ── Cart drawer ───────────────────────────────────────────────────────────

    _openCartDrawer() {
      // ── 1. Dispatch common cart events ──────────────────────────────────────
      // Different themes listen for different event names; fire them all.
      [
        'cart:open',
        'cart:toggle',
        'cart-drawer:open',
        'CartDrawer:open',
        'drawer:open',
        'ajaxCart.afterCartLoad',
      ].forEach(name => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true }));
      });

      // ── 2. Try common DOM-based drawer patterns ──────────────────────────────
      const drawerSelectors = [
        '#cart-drawer',
        '#CartDrawer',
        '[data-cart-drawer]',
        '[data-drawer="cart"]',
        '.cart-drawer',
        '.js-cart-drawer',
        '.ajax-cart',
      ];
      for (const sel of drawerSelectors) {
        const el = document.querySelector(sel);
        if (el) {
          el.classList.add('is-open', 'active', 'open');
          el.removeAttribute('hidden');
          el.setAttribute('aria-hidden', 'false');
          break;
        }
      }

      // ── 3. Section Rendering API — reload cart drawer section if it exists ──
      const cartSection = document.querySelector('[id^="shopify-section-"][id*="cart"]');
      const cartSectionId = cartSection?.id?.replace('shopify-section-', '');
      if (cartSectionId) {
        fetch(`/?sections=${cartSectionId}`, { headers: { 'Accept': 'application/json' } })
          .then(r => r.json())
          .then(sections => {
            const html = sections[cartSectionId];
            if (!html) return;
            const parser = new DOMParser();
            const newDoc = parser.parseFromString(html, 'text/html');
            const newInner = newDoc.querySelector(`#shopify-section-${cartSectionId}`);
            if (newInner && cartSection) {
              cartSection.innerHTML = newInner.innerHTML;
            }
          })
          .catch(() => {});
      }
    }

    // ── Variant change event ─────────────────────────────────────────────────

    _dispatchVariantChange(variant) {
      document.dispatchEvent(new CustomEvent(`vpv:variant-change:${this._sectionId}`, {
        bubbles: true,
        detail: { variant },
      }));
    }
  }


  // ═══════════════════════════════════════════════════════════════════════════
  //  Register — guard against double-registration on live theme hot reloads
  // ═══════════════════════════════════════════════════════════════════════════

  if (!customElements.get('vpv-simple-gallery')) {
    customElements.define('vpv-simple-gallery', VpvSimpleGallery);
  }

  if (!customElements.get('vpv-simple-form')) {
    customElements.define('vpv-simple-form', VpvSimpleForm);
  }

})();
